import React, { Component, Fragment } from "react";
import { Row, Col, Card, Button, Badge, Modal, Table } from "react-bootstrap";
import swal from "sweetalert";

import { db, storage } from "../../../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  addDoc,
  collectionGroup,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";

import Toast, {
  notifyTopRight,
  notifyError,
  notifyWarning,
} from "../../components/toast/SetToast";
import { AuthContext } from "../../../context/AuthContext";
import { normalizeSleepStudyReportDocs } from "./cpapFollowupFileUtils";
import {
  FOLLOWUP_TYPE_LIST,
  getFollowupTypeOptionsForNode,
  getNextFollowupTypeForContinueTherapy as getNextFollowupTypeForNode,
  isFollowupTypeAllowedForNode,
} from "./cpapFollowupTypeUtils";
import {
  getCpapFollowupRole,
  isStaffCpapFollowupViewer,
} from "./cpapFollowupAccess";
import {
  getCpapSideEffectOptionsForDisplay,
  getSavedNonPdfCpapSideEffectValues,
  updateCpapSideEffectSelection,
} from "./cpapFollowupSymptomUtils";
import AnnualMaskPurchaseEventSection from "./AnnualMaskPurchaseEventSection";

import { filterCpapFollowupRecords, filterCpapFollowupCustomers, getCpapFollowupAccessKey, isCpapFollowupNodeCustomerScopedUser } from "./cpapFollowupAccess";
import { loadCpapFollowupScope } from "./cpapFollowupDataAccess";

const COLLECTION_NAME = "cpap-followup";

const STATUS_OPTIONS = [
  {
    value: "CPAP Follow Up",
    label: "CPAP Follow Up",
    helper: "Technician needs to follow up with patient",
    color: "#A2D2FF",
    badge: "primary",
  },
  {
    value: "Technical Solution",
    label: "Technical Solution",
    helper: "Technician is helping solve CPAP usage problems",
    color: "#FFD8A8",
    badge: "warning",
  },
  {
    value: "Effective Use",
    label: "Effective Use",
    helper: "Patient is using CPAP well",
    color: "#CAFFBF",
    badge: "success",
  },
  {
    value: "CPAP-Consult",
    label: "CPAP-Consult",
    helper: "Patient needs doctor consult for next treatment step",
    color: "#FFADAD",
    badge: "danger",
  },
  {
    value: "Discontinued",
    label: "Discontinued",
    helper: "Patient stopped using CPAP",
    color: "#D1D5DB",
    badge: "secondary",
  },
];

const DOCTOR_ACTION_OPTIONS = [
  { value: "No Doctor Action", label: "รอการประเมินจากแพทย์" },
  { value: "Adjust Pressure", label: "ปรับ Pressure" },
  { value: "Doctor Appointment", label: "นัดพบแพทย์" },
  { value: "Doctor Phone Consult", label: "แพทย์โทรติดตามอาการ" },
  { value: "Continue Therapy", label: "ใช้เครื่องต่อ" },
];

const CPAP_TYPE_OPTIONS = ["CPAP", "Auto-CPAP", "BiPAP"];

const MASK_TYPE_OPTIONS = [
  "กรุณาเลือก",
  "Nasal Mask AirFit N20",
  "Nasal Mask AirFit N30",
  "Nasal Mask AirFit N30i",
  "Nasal Pillow AirFit P10",
  "Nasal Pillow AirFit P30i",
  "Full Face Mask AirFit F20",
  "Full Face Mask AirTouch F20",
  "Nasal Mask ResComf",
  "Full Face Mask ResComf",
  "Nasal Mask Wizard 310",
  "Full Face Mask Wizard 320",
  "Topson Full Face FM50 Mask",
  "Topson Nasal NM40 Mask",
  "Topson Nasal NM50 Mask",
  "Philips Wisp Nasal Mask",
  "Philips Pico Nasal Mask",
  "Philips Amara Gel Full Face Mask",
  "Nasal Mask",
  "Nasal Pillow",
  "Full Face Mask",
  "Oronasal Mask",
  "Other",
];

const MASK_SIZE_OPTIONS = ["กรุณาเลือก", "XS", "S", "M", "L", "XL", "Other"];

const LEAK_ASSESSMENT_OPTIONS = [
  "กรุณาเลือก",
  "อยู่ในเกณฑ์ดี",
  "สูง ควรได้รับการแก้ไข",
];

const WAKE_FEELING_OPTIONS = [
  "สดชื่นขึ้นมาก",
  "สดชื่นขึ้น",
  "เหมือนเดิม",
  "แย่ลง",
];

const DAYTIME_SLEEPINESS_OPTIONS = [
  "ดีขึ้นมาก",
  "ดีขึ้น",
  "เหมือนเดิม",
  "แย่ลง",
];

const formatLocalDate = (dateValue = new Date()) => {
  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);

  if (!date || isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const toDateString = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  if (value.toDate) return formatLocalDate(value.toDate());
  if (value instanceof Date) return formatLocalDate(value);
  return "";
};

const safeText = (value) =>
  value === undefined || value === null ? "" : String(value);

const cleanForFirestore = (value) => {
  if (value === undefined) return null;
  if (value === null) return null;
  if (Array.isArray(value)) return value.map(cleanForFirestore);
  if (value instanceof Date) return value.toISOString();
  if (value && typeof value === "object") {
    if (
      typeof value.toDate === "function" ||
      typeof value.seconds === "number" ||
      typeof value.nanoseconds === "number"
    ) {
      return value;
    }

    return Object.entries(value).reduce((result, [key, item]) => {
      result[key] = cleanForFirestore(item);
      return result;
    }, {});
  }

  return value;
};

const parseAverageAhi = (value) => {
  if (value === undefined || value === null) return null;

  const text = String(value).trim();

  if (!text) return null;

  // Supports values like "35", "35.2", "AHI 35", "35 events/hour"
  const number = parseFloat(text.replace(/[^\d.-]/g, ""));

  if (Number.isNaN(number)) return null;

  return number;
};

const getStatusConfig = (status) =>
  STATUS_OPTIONS.find((item) => item.value === status) || STATUS_OPTIONS[0];

const getDoctorActionLabel = (value) => {
  const item = DOCTOR_ACTION_OPTIONS.find((option) => option.value === value);
  return item ? item.label : value || "-";
};

const getEmptyForm = () => ({
  uuid: "",
  id: "",
  title: "",
  date: formatLocalDate(new Date()),

  order: "",
  hn: "",
  firstname: "",
  lastname: "",
  patientName: "",
  phone: "",
  hospitalRef: "",
  node: "",
  nodeId: "",
  customer: "",

  underlyingDisease: "",
  prePurchaseAhi: "",

  type: "CPAP Follow-Up 1M",
  status: "CPAP Follow Up",
  doctorActionStatus: "No Doctor Action",
  technicianConsult: "กรุณาเลือก",
  doctorConsult: "กรุณาเลือก",

  latestTherapyStatusRecord: null,

  serialNumber: "",
  brand: "",
  model: "",
  cpapType: "Auto-CPAP",
  maskType: "กรุณาเลือก",
  maskSize: "กรุณาเลือก",

  minPressure: "",
  maxPressure: "",
  currentRampTime: "",
  humidifierSystem: "",
  averageLeak: "",

  dataPeriod: "",
  dateSummary: "",

  usageMoreThan4HoursPercent: "",
  averageUsageHours: "",
  averageAhi: "",
  pressure95Percent: "",
  leakRate: "",
  leakAssessment: "กรุณาเลือก",
  essScore: "",
  currentWeight: "",
  weightChange: "",

  wakeFeeling: "",
  daytimeSleepiness: "",

  note: "",

  sideEffects: [],
  sideEffectsOther: "",
  usageReports: [],
  questionnaires: [],
  sleepStudyReports: [],
  annualMaskPurchase: null,

  createdBy: "",
  createdAt: null,
  updatedBy: "",
  updatedAt: null,
});

class CPAPFollowupTable extends Component {
  static contextType = AuthContext;

  usageFileRef = React.createRef();
  questionnaireFileRef = React.createRef();

  state = {
    followupList: [],
    filteredList: [],
    nodeList: [],
    customerList: [],
    staffAdminProfile: null,
    technicianList: ["กรุณาเลือก"],
    doctorList: ["กรุณาเลือก"],
    isLoading: true,
    isUploading: false,

    isFilePreviewModal: false,
    previewFile: null,

    selectedFollowup: null,
    isDetailModal: false,

    activeTab: "patient",
    isCreateMode: false,
    isEditMode: false,
    editForm: null,
    isSaving: false,

    sortField: "date",
    sortDirection: "asc",
    currentPage: 1,
    pageSize: 20,

    filters: {
      search: "",
      node: "All Node",
      status: "All Status",
      type: "All Type",
      averageAhi: "All Average AHI",
      technician: "All Technician",
      doctor: "All Doctor",
      doctorActionStatus: "All Doctor Action",
      dateFrom: "",
      dateTo: "",
    },
  };

  componentDidMount() {
    this._isMounted = true;
    this.fetchData();
  }

  componentDidUpdate() {
    if (this._followupAccessKey !== getCpapFollowupAccessKey(this.context && this.context.currentUser)) {
      this._followupAccessKey = getCpapFollowupAccessKey(this.context && this.context.currentUser);
      this.setState({ isDetailModal: false, isEditMode: false, selectedFollowup: null, editForm: null, isFilePreviewModal: false, previewFile: null });
      this.fetchData();
    }
  }

  filterAccessibleRecords = (records) => filterCpapFollowupRecords({
    records, currentUser: this.context && this.context.currentUser,
    adminData: this.state.staffAdminProfile, nodes: this.state.nodeList,
  });

  componentWillUnmount() {
    this._isMounted = false;
  }

  getCurrentUserName = () => {
    if (
      this.context &&
      this.context.currentUser &&
      this.context.currentUser.displayName
    ) {
      return this.context.currentUser.displayName;
    }

    return "";
  };

  getCurrentRole = () =>
    getCpapFollowupRole(this.context && this.context.currentUser);

  isStaffReadOnly = () =>
    isStaffCpapFollowupViewer(
      this.context && this.context.currentUser,
    );

  getFileCount = (item) => {
    const usageReports = Array.isArray(item.usageReports)
      ? item.usageReports
      : [];

    const questionnaires = Array.isArray(item.questionnaires)
      ? item.questionnaires
      : [];

    return usageReports.length + questionnaires.length;
  };

  addDaysToDateString = (dateString, days) => {
    const baseDate = dateString
      ? new Date(`${dateString}T00:00:00`)
      : new Date();

    if (isNaN(baseDate.getTime())) {
      return formatLocalDate(new Date());
    }

    baseDate.setDate(baseDate.getDate() + days);

    return formatLocalDate(baseDate);
  };

  getNextFollowupTypeForContinueTherapy = (currentType, nodeName) =>
    getNextFollowupTypeForNode(currentType, nodeName, this.state.nodeList);

  buildDoctorDecisionRecord = (form, doctorActionValue) => {
    return {
      source: "doctor_decision_before_reschedule",
      date: form.date || "",
      type: form.type || "",
      status: form.status || "",
      minPressure: form.minPressure || "",
      maxPressure: form.maxPressure || "",
      currentRampTime: form.currentRampTime || "",
      doctorActionStatus: doctorActionValue || "",
      note: form.note || "",
      recordedAt: new Date().toISOString(),
    };
  };

  buildTitle = (ep = {}) => {
    const hn = safeText(ep.hn);
    const name = `${safeText(ep.firstname)} ${safeText(ep.lastname)}`.trim();
    const type = safeText(ep.type);

    if (hn && name) return `${hn} - ${name}`;
    if (hn) return `${hn} - ${type}`;
    return type || "CPAP Follow-Up";
  };

  getFollowupDocId = (item = {}) =>
    safeText(item.docId || item.uuid || item.id).trim();

  getFollowupRecordAction = (payload, fallbackAction = "update") => {
    const ep = payload.extendedProps || {};
    const decisionRecord = ep.latestTherapyStatusRecord || {};

    if (
      decisionRecord.source === "doctor_decision_before_reschedule" ||
      decisionRecord.doctorActionStatus
    ) {
      return "doctor_action";
    }

    const previous = this.state.selectedFollowup || {};

    if (
      previous.doctorActionStatus &&
      previous.doctorActionStatus !== ep.doctorActionStatus
    ) {
      return "doctor_action";
    }

    return fallbackAction;
  };

  addFollowupRecord = async (payload, action, userName) => {
    const ep = payload.extendedProps || {};
    const hn = safeText(ep.hn).trim();
    const decisionRecord = ep.latestTherapyStatusRecord || {};
    const doctorActionStatus =
      action === "doctor_action"
        ? decisionRecord.doctorActionStatus || ep.doctorActionStatus || ""
        : ep.doctorActionStatus || "";

    if (!hn) {
      throw new Error("Cannot create follow-up history without HN");
    }

    const recordRef = doc(collection(db, "patients", hn, "followupRecord"));

    await setDoc(recordRef, {
      source: COLLECTION_NAME,
      action,
      followupUuid: ep.uuid || payload.id || "",
      title: payload.title || "",
      order: ep.order || "",
      hn,
      firstname: ep.firstname || "",
      lastname: ep.lastname || "",
      phone: ep.phone || "",
      node: ep.node || "",
      customer: ep.customer || "",
      type: ep.type || "",
      status: ep.status || "",
      doctorActionStatus,
      technicianConsult: ep.technicianConsult || "",
      doctorConsult: ep.doctorConsult || "",
      serialNumber: ep.serialNumber || "",
      brand: ep.brand || "",
      model: ep.model || "",
      cpapType: ep.cpapType || "",
      cpapReportType: ep.cpapReportType || "",
      usageMoreThan4HoursPercent: ep.usageMoreThan4HoursPercent || "",
      averageUsageHours: ep.averageUsageHours || "",
      averageAhi: ep.averageAhi || "",
      pressure95Percent: ep.pressure95Percent || "",
      averageLeak: ep.averageLeak || "",
      leakAssessment: ep.leakAssessment || "",
      essScore: ep.essScore || "",
      currentWeight: ep.currentWeight || "",
      weightChange: ep.weightChange || "",
      wakeFeeling: ep.wakeFeeling || "",
      daytimeSleepiness: ep.daytimeSleepiness || "",
      sideEffects: Array.isArray(ep.sideEffects) ? ep.sideEffects : [],
      sideEffectsOther: ep.sideEffectsOther || "",
      note: ep.note || "",
      start: payload.start || null,
      date: ep.date || ep.followupDate || "",
      usageReports: Array.isArray(ep.usageReports) ? ep.usageReports : [],
      questionnaires: Array.isArray(ep.questionnaires) ? ep.questionnaires : [],
      recordBy: userName || "",
      recordAt: serverTimestamp(),
      extendedProps: cleanForFirestore(ep),
    });
  };

  addFollowupRecordSafely = async (payload, action, userName) => {
    try {
      await this.addFollowupRecord(payload, action, userName);
    } catch (error) {
      console.error("Create follow-up history error:", error);
      notifyWarning("Saved, but cannot create follow-up history record");
    }
  };

  mapDocToTableItem = (docSnap) => {
    const data = docSnap.data();
    const ep = data.extendedProps || {};
    const date = toDateString(data.start || ep.date || ep.followupDate);
    const docId = docSnap.id;

    const patientName = `${safeText(ep.firstname)} ${safeText(
      ep.lastname,
    )}`.trim();

    return {
      uuid: docId,
      docId,
      originalUuid: safeText(ep.uuid || data.id),
      id: data.id || docSnap.id,
      title: data.title || "",
      date,

      order: safeText(ep.order),
      hn: safeText(ep.hn),
      firstname: safeText(ep.firstname),
      lastname: safeText(ep.lastname),
      patientName,
      phone: safeText(ep.phone),
      hospitalRef: safeText(ep.hospitalRef),
      node: safeText(ep.node),
      nodeId: safeText(ep.nodeId),
      customer: safeText(ep.customer),

      underlyingDisease: safeText(ep.underlyingDisease),
      prePurchaseAhi: safeText(ep.prePurchaseAhi),

      type: safeText(ep.type),
      status: safeText(ep.status),
      doctorActionStatus: safeText(ep.doctorActionStatus),
      technicianConsult: safeText(ep.technicianConsult),
      doctorConsult: safeText(ep.doctorConsult),

      latestTherapyStatusRecord: ep.latestTherapyStatusRecord || null,

      serialNumber: safeText(ep.serialNumber),
      brand: safeText(ep.brand),
      model: safeText(ep.model),
      cpapType: safeText(ep.cpapType),
      maskType: safeText(ep.maskType),
      maskSize: safeText(ep.maskSize),

      minPressure: safeText(ep.minPressure),
      maxPressure: safeText(ep.maxPressure),
      currentRampTime: safeText(ep.currentRampTime),
      humidifierSystem: safeText(ep.humidifierSystem),
      averageLeak: safeText(ep.averageLeak),

      dataPeriod: safeText(ep.dataPeriod),
      dateSummary: safeText(ep.dateSummary),

      usageMoreThan4HoursPercent: safeText(ep.usageMoreThan4HoursPercent),
      averageUsageHours: safeText(ep.averageUsageHours),
      averageAhi: safeText(ep.averageAhi),
      pressure95Percent: safeText(ep.pressure95Percent),
      leakRate: safeText(ep.leakRate),
      leakAssessment: safeText(ep.leakAssessment),
      essScore: safeText(ep.essScore),
      currentWeight: safeText(ep.currentWeight),
      weightChange: safeText(ep.weightChange),

      wakeFeeling: safeText(ep.wakeFeeling),
      daytimeSleepiness: safeText(ep.daytimeSleepiness),

      note: safeText(ep.note),

      sideEffects: Array.isArray(ep.sideEffects) ? ep.sideEffects : [],
      sideEffectsOther: safeText(ep.sideEffectsOther),
      usageReports: Array.isArray(ep.usageReports) ? ep.usageReports : [],
      questionnaires: Array.isArray(ep.questionnaires) ? ep.questionnaires : [],
      sleepStudyReports: [],

      createdBy: safeText(data.createdBy),
      createdAt: data.createdAt || null,
      updatedBy: safeText(data.updatedBy),
      updatedAt: data.updatedAt || null,
    };
  };

  loadAllPatientCpapFilesMap = async (followupList = []) => {
    const hnList = [
      ...new Set(
        followupList
          .map((item) => safeText(item.hn).trim())
          .filter(Boolean),
      ),
    ];

    const fileMap = {};

    await Promise.all(
      hnList.map(async (hn) => {
        try {
          const filesRef = collection(db, "patients", hn, "cpapFollowupFiles");

          let fileSnap;

          try {
            fileSnap = await getDocs(
              query(filesRef, orderBy("uploadedAt", "desc")),
            );
          } catch (error) {
            fileSnap = await getDocs(filesRef);
          }

          const usageReports = [];
          const questionnaires = [];

          fileSnap.forEach((fileDoc) => {
            const data = fileDoc.data();

            const fileData = {
              id: fileDoc.id,
              fileName: data.fileName || "",
              savedFileName: data.savedFileName || "",
              storagePath: data.storagePath || "",
              url: data.url || "",
              uploadedAt: data.uploadedAt || "",
              uploadedBy: data.uploadedBy || "",
              hn: data.hn || hn,
              followupUuid: data.followupUuid || "",
              fileType: data.fileType || "",
            };

            if (data.fileType === "questionnaires") {
              questionnaires.push(fileData);
            } else {
              usageReports.push(fileData);
            }
          });

          fileMap[hn] = {
            usageReports,
            questionnaires,
          };
        } catch (error) {
          console.error("Load CPAP files map error:", hn, error);

          fileMap[hn] = {
            usageReports: [],
            questionnaires: [],
          };
        }
      }),
    );

    return fileMap;
  };

  loadPatientSleepStudyReports = async (hn) => {
    if (!hn) return [];

    try {
      const reportRef = collection(db, "patients", hn, "report");
      const reportSnap = await getDocs(reportRef);

      return normalizeSleepStudyReportDocs(reportSnap.docs, hn);
    } catch (error) {
      console.error("Load patient sleep study reports error:", error);
      return [];
    }
  };

  loadAllPatientSleepStudyReportsMap = async (followupList = []) => {
    const hnList = [
      ...new Set(
        followupList
          .map((item) => safeText(item.hn).trim())
          .filter(Boolean),
      ),
    ];
    const reportMap = {};

    await Promise.all(
      hnList.map(async (hn) => {
        reportMap[hn] = await this.loadPatientSleepStudyReports(hn);
      }),
    );

    return reportMap;
  };

  fetchData = async () => {
    const currentUser = this.context && this.context.currentUser;
    const accessKey = getCpapFollowupAccessKey(currentUser);
    this._followupAccessKey = accessKey;
    const requestId = this._followupRequestId = (this._followupRequestId || 0) + 1;
    try {
      this.setState({ isLoading: true, followupList: [], filteredList: [], nodeList: [], staffAdminProfile: null, customerList: [] });

      const [followupSnap, nodeSnap, techSnap, doctorSnap] = await Promise.all([
        getDocs(collection(db, COLLECTION_NAME)),
        getDocs(collection(db, "nodes")),
        getDocs(collection(db, "sleeptechnicians")),
        getDocs(collection(db, "doctors")),
      ]);

      if (!this._isMounted || requestId !== this._followupRequestId || accessKey !== getCpapFollowupAccessKey(this.context && this.context.currentUser)) return;

      let nodes = nodeSnap.docs.map((nodeDoc) => ({
        id: nodeDoc.id,
        ...nodeDoc.data(),
      }));

      nodes.sort((a, b) => {
        const aIndex = Number(a.nodeIndex || 0);
        const bIndex = Number(b.nodeIndex || 0);

        if (aIndex !== bIndex) return aIndex - bIndex;
        return safeText(a.nodename).localeCompare(safeText(b.nodename));
      });

      const currentRole = this.getCurrentRole();
      const currentUserName = this.getCurrentUserName();
      const scope = await loadCpapFollowupScope(currentUser, nodes);
      if (!this._isMounted || requestId !== this._followupRequestId || accessKey !== getCpapFollowupAccessKey(this.context && this.context.currentUser)) return;

      nodes = scope.nodes;
      const staffAdminProfile = scope.adminData;

      const allowedNodeNames = nodes.map((item) => item.nodename);

      let followupList = followupSnap.docs.map(this.mapDocToTableItem);

      if (currentRole === "sleep_physician") {
        followupList = followupList.filter(
          (item) => item.doctorConsult === currentUserName,
        );
      } else if (currentRole === "sale") {
        followupList = followupList.filter((item) =>
          allowedNodeNames.includes(item.node),
        );
      }

      followupList = filterCpapFollowupRecords({ records: followupList, currentUser, adminData: staffAdminProfile, nodes });

      const [patientFilesMap, sleepStudyReportsMap] = await Promise.all([
        this.loadAllPatientCpapFilesMap(followupList),
        this.loadAllPatientSleepStudyReportsMap(followupList),
      ]);

      followupList = followupList.map((item) => {
        const patientFiles = patientFilesMap[item.hn];
        const sleepStudyReports = sleepStudyReportsMap[item.hn] || [];

        if (!patientFiles) {
          return {
            ...item,
            sleepStudyReports,
          };
        }

        return {
          ...item,
          usageReports: patientFiles.usageReports,
          questionnaires: patientFiles.questionnaires,
          sleepStudyReports,
        };
      });

      const technicianList = [
        "กรุณาเลือก",
        ...techSnap.docs
          .filter((item) => item.data().status !== "Unavailable")
          .map((item) => item.data().name || item.data().nickname || item.id)
          .filter(Boolean),
      ];

      const doctorList =
        currentRole === "sleep_physician"
          ? ["กรุณาเลือก", currentUserName].filter(Boolean)
          : [
              "กรุณาเลือก",
              ...doctorSnap.docs
                .map((item) => item.data().name || item.id)
                .filter(Boolean),
            ];

      if (!this._isMounted || requestId !== this._followupRequestId || accessKey !== getCpapFollowupAccessKey(this.context && this.context.currentUser)) return;

      this.setState(
        {
          followupList,
          filteredList: followupList,
          nodeList: nodes,
          staffAdminProfile,
          technicianList,
          doctorList,
          filters:
            currentRole === "sleep_physician"
              ? {
                  ...this.state.filters,
                  node: "All Node",
                  doctor: currentUserName,
                }
              : this.state.filters,
          isLoading: false,
        },
        this.applyFilters,
      );
    } catch (error) {
      if (requestId !== this._followupRequestId || accessKey !== getCpapFollowupAccessKey(this.context && this.context.currentUser)) return;
      console.error(error);
      notifyError("Cannot load CPAP follow-up table");
      this.setState({ isLoading: false });
    }
  };

  handleFilterChange = (name, value) => {
    this.setState(
      (prevState) => ({
        filters: {
          ...prevState.filters,
          [name]: value,
        },
        currentPage: 1,
      }),
      this.applyFilters,
    );
  };

  resetFilters = () => {
    this.setState(
      {
        filters: {
          search: "",
          node: "All Node",
          status: "All Status",
          type: "All Type",
          averageAhi: "All Average AHI",
          technician: "All Technician",
          doctor:
            this.getCurrentRole() === "sleep_physician"
              ? this.getCurrentUserName()
              : "All Doctor",
          doctorActionStatus: "All Doctor Action",
          dateFrom: "",
          dateTo: "",
        },
        currentPage: 1,
      },
      this.applyFilters,
    );
  };

  applyFilters = () => {
    const { followupList, filters, sortField, sortDirection } = this.state;

    const search = safeText(filters.search).toLowerCase().trim();

    let list = followupList.filter((item) => {
      if (filters.node !== "All Node" && item.node !== filters.node) {
        return false;
      }

      if (filters.status !== "All Status" && item.status !== filters.status) {
        return false;
      }

      if (filters.type !== "All Type" && item.type !== filters.type) {
        return false;
      }

      if (filters.averageAhi !== "All Average AHI") {
        const averageAhi = parseAverageAhi(item.averageAhi);

        if (filters.averageAhi === "EMPTY_OR_ZERO") {
          if (!(averageAhi === null || averageAhi === 0)) {
            return false;
          }
        }

        if (filters.averageAhi === "LESS_THAN_30") {
          if (!(averageAhi !== null && averageAhi > 0 && averageAhi < 30)) {
            return false;
          }
        }

        if (filters.averageAhi === "BETWEEN_30_60") {
          if (!(averageAhi !== null && averageAhi >= 30 && averageAhi <= 60)) {
            return false;
          }
        }

        if (filters.averageAhi === "MORE_THAN_60") {
          if (!(averageAhi !== null && averageAhi > 60)) {
            return false;
          }
        }
      }

      if (
        filters.technician !== "All Technician" &&
        item.technicianConsult !== filters.technician
      ) {
        return false;
      }

      if (
        filters.doctor !== "All Doctor" &&
        item.doctorConsult !== filters.doctor
      ) {
        return false;
      }

      if (
        filters.doctorActionStatus !== "All Doctor Action" &&
        item.doctorActionStatus !== filters.doctorActionStatus
      ) {
        return false;
      }

      if (filters.dateFrom && item.date < filters.dateFrom) {
        return false;
      }

      if (filters.dateTo && item.date > filters.dateTo) {
        return false;
      }

      if (search) {
        const searchTarget = [
          item.order,
          item.hn,
          item.firstname,
          item.lastname,
          item.patientName,
          item.phone,
          item.hospitalRef,
          item.node,
          item.customer,
          item.type,
          item.status,
          item.technicianConsult,
          item.doctorConsult,
          getDoctorActionLabel(item.doctorActionStatus),
          item.sideEffectsOther,
          item.note,
        ]
          .join(" ")
          .toLowerCase();

        if (!searchTarget.includes(search)) {
          return false;
        }
      }

      return true;
    });

    list.sort((a, b) => {
      let aValue = safeText(a[sortField]);
      let bValue = safeText(b[sortField]);

      if (sortField === "fileCount") {
        aValue = this.getFileCount(a);
        bValue = this.getFileCount(b);
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      const result = String(aValue).localeCompare(String(bValue));
      return sortDirection === "asc" ? result : -result;
    });

    this.setState((prevState) => {
      const totalPages = Math.max(
        1,
        Math.ceil(list.length / prevState.pageSize),
      );
      const currentPage = Math.min(prevState.currentPage, totalPages);

      return {
        filteredList: list,
        currentPage,
      };
    });
  };

  handleSort = (field) => {
    this.setState((prevState) => {
      const isSameField = prevState.sortField === field;

      return {
        sortField: field,
        sortDirection: isSameField
          ? prevState.sortDirection === "asc"
            ? "desc"
            : "asc"
          : "asc",
        currentPage: 1,
      };
    }, this.applyFilters);
  };

  goToPage = (page) => {
    const totalPages = Math.max(
      1,
      Math.ceil(this.state.filteredList.length / this.state.pageSize),
    );

    const nextPage = Math.max(1, Math.min(page, totalPages));

    this.setState({ currentPage: nextPage });
  };

  renderPagination = () => {
    const { filteredList, currentPage, pageSize } = this.state;
    const totalItems = filteredList.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);

    if (totalItems <= pageSize) {
      return (
        <div
          className="d-flex justify-content-between align-items-center px-3 py-2"
          style={{ borderTop: "1px solid #e9ecef", fontSize: 12 }}
        >
          <div className="text-muted">Showing {totalItems} event(s)</div>
        </div>
      );
    }

    return (
      <div
        className="d-flex justify-content-between align-items-center flex-wrap px-3 py-2"
        style={{ borderTop: "1px solid #e9ecef", gap: 8 }}
      >
        <div className="text-muted" style={{ fontSize: 12 }}>
          Showing {startItem}-{endItem} of {totalItems} events · Page{" "}
          {currentPage} of {totalPages}
        </div>

        <div className="d-flex align-items-center" style={{ gap: 6 }}>
          <Button
            variant="light"
            size="sm"
            onClick={() => this.goToPage(1)}
            disabled={currentPage === 1}
            style={{ border: "1px solid #ced4da" }}
          >
            First
          </Button>

          <Button
            variant="light"
            size="sm"
            onClick={() => this.goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            style={{ border: "1px solid #ced4da" }}
          >
            Previous
          </Button>

          <Button
            variant="light"
            size="sm"
            onClick={() => this.goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            style={{ border: "1px solid #ced4da" }}
          >
            Next
          </Button>

          <Button
            variant="light"
            size="sm"
            onClick={() => this.goToPage(totalPages)}
            disabled={currentPage === totalPages}
            style={{ border: "1px solid #ced4da" }}
          >
            Last
          </Button>
        </div>
      </div>
    );
  };

  renderSortIcon = (field) => {
    if (this.state.sortField !== field) {
      return <span className="text-muted ml-1">↕</span>;
    }

    return (
      <span className="ml-1">
        {this.state.sortDirection === "asc" ? "↑" : "↓"}
      </span>
    );
  };

  openCreateModal = () => {
    if (this.isStaffReadOnly()) {
      notifyWarning("Staff can view CPAP follow-up only");
      return;
    }

    this.setState({
      selectedFollowup: null,
      editForm: getEmptyForm(),
      activeTab: "patient",
      isCreateMode: true,
      isEditMode: true,
      isDetailModal: true,
      customerList: [],
    });
  };

  openDetailModal = async (item) => {
    let nextItem = { ...item };

    try {
      if (item && item.hn) {
        const [patientFiles, sleepStudyReports] = await Promise.all([
          this.loadPatientCpapFiles(item.hn),
          this.loadPatientSleepStudyReports(item.hn),
        ]);

        nextItem = {
          ...nextItem,
          usageReports: patientFiles.usageReports,
          questionnaires: patientFiles.questionnaires,
          sleepStudyReports,
        };
      }
    } catch (error) {
      console.error("Open detail file query error:", error);
    }

    this.setState({
      selectedFollowup: nextItem,
      editForm: { ...nextItem },
      isDetailModal: true,
      isEditMode: false,
      activeTab: "patient",
    });
  };
  closeDetailModal = () => {
    this.setState({
      selectedFollowup: null,
      editForm: null,
      activeTab: "patient",
      isCreateMode: false,
      isEditMode: false,
      isDetailModal: false,
      customerList: [],
    });
  };

  formatAuditDateTime = (value) => {
    if (!value) return "-";

    let dateValue = null;

    if (value.toDate) {
      dateValue = value.toDate();
    } else if (value.seconds) {
      dateValue = new Date(value.seconds * 1000);
    } else if (value instanceof Date) {
      dateValue = value;
    } else {
      dateValue = new Date(value);
    }

    if (!dateValue || isNaN(dateValue.getTime())) return "-";

    return dateValue.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  renderStatusBadge = (status) => {
    const statusConfig = getStatusConfig(status);

    return (
      <Badge
        variant={statusConfig.badge}
        style={{
          backgroundColor: statusConfig.color,
          color: "#3b2b46",
          borderRadius: 12,
          padding: "6px 9px",
          fontSize: 11,
          whiteSpace: "nowrap",
        }}
      >
        {statusConfig.label}
      </Badge>
    );
  };

  renderSummaryCards = () => {
    const { filteredList } = this.state;

    const total = filteredList.length;
    const cpapFollowup = filteredList.filter(
      (item) => item.status === "CPAP Follow Up",
    ).length;
    const technical = filteredList.filter(
      (item) => item.status === "Technical Solution",
    ).length;
    const consult = filteredList.filter(
      (item) => item.status === "CPAP-Consult",
    ).length;
    const effective = filteredList.filter(
      (item) => item.status === "Effective Use",
    ).length;

    const cards = [
      { label: "Total", value: total },
      { label: "CPAP Follow Up", value: cpapFollowup },
      { label: "Technical Solution", value: technical },
      { label: "CPAP-Consult", value: consult },
      { label: "Effective Use", value: effective },
    ];

    return (
      <Row>
        {cards.map((item) => (
          <Col xl={2} md={4} sm={6} key={item.label}>
            <Card
              style={{
                border: "1px solid #e9ecef",
                borderRadius: 10,
                boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
              }}
            >
              <Card.Body style={{ padding: "12px 14px" }}>
                <div
                  style={{
                    fontSize: 12,
                    color: "#6c757d",
                    marginBottom: 4,
                  }}
                >
                  {item.label}
                </div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: "#2f365f",
                    lineHeight: 1,
                  }}
                >
                  {item.value}
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  renderFilters = () => {
    const { filters } = this.state;
    const isSleepPhysician = this.getCurrentRole() === "sleep_physician";
    const currentUserName = this.getCurrentUserName();

    const nodeOptions = [
      "All Node",
      ...this.state.nodeList.map((item) => item.nodename),
    ];

    const doctorOptions = isSleepPhysician
      ? [currentUserName].filter(Boolean)
      : this.state.doctorList.filter((item) => item !== "กรุณาเลือก");

    return (
      <div className="mb-3" style={{ borderRadius: 10 }}>
        <Card.Body style={{ padding: 14 }}>
          <Row>
            <Col lg={3} md={6} className="mb-2">
              <label className="mb-1 small text-muted">Search</label>
              <input
                className="form-control form-control-sm"
                placeholder="Order, HN, name, phone, note..."
                value={filters.search}
                onChange={(e) =>
                  this.handleFilterChange("search", e.target.value)
                }
              />
            </Col>

            {!isSleepPhysician && (
              <Col lg={2} md={6} className="mb-2">
                <label className="mb-1 small text-muted">Node</label>
                <select
                  className="form-control form-control-sm"
                  value={filters.node}
                  onChange={(e) =>
                    this.handleFilterChange("node", e.target.value)
                  }
                >
                  {nodeOptions.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </Col>
            )}

            <Col lg={2} md={6} className="mb-2">
              <label className="mb-1 small text-muted">Status</label>
              <select
                className="form-control form-control-sm"
                value={filters.status}
                onChange={(e) =>
                  this.handleFilterChange("status", e.target.value)
                }
              >
                <option value="All Status">All Status</option>
                {STATUS_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </Col>

            <Col lg={2} md={6} className="mb-2">
              <label className="mb-1 small text-muted">Type</label>
              <select
                className="form-control form-control-sm"
                value={filters.type}
                onChange={(e) =>
                  this.handleFilterChange("type", e.target.value)
                }
              >
                <option value="All Type">All Type</option>
                {FOLLOWUP_TYPE_LIST.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </Col>

            <Col lg={3} md={6} className="mb-2">
              <label className="mb-1 small text-muted">Doctor Next Step</label>
              <select
                className="form-control form-control-sm"
                value={filters.doctorActionStatus}
                onChange={(e) =>
                  this.handleFilterChange("doctorActionStatus", e.target.value)
                }
              >
                <option value="All Doctor Action">All Doctor Action</option>
                {DOCTOR_ACTION_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </Col>

            <Col lg={2} md={6} className="mb-2">
              <label className="mb-1 small text-muted">Average AHI</label>
              <select
                className="form-control form-control-sm"
                value={filters.averageAhi}
                onChange={(e) =>
                  this.handleFilterChange("averageAhi", e.target.value)
                }
              >
                <option value="All Average AHI">All Average AHI</option>
                <option value="EMPTY_OR_ZERO">ว่าง / 0</option>
                <option value="LESS_THAN_30">น้อยกว่า 30</option>
                <option value="BETWEEN_30_60">30 - 60</option>
                <option value="MORE_THAN_60">มากกว่า 60</option>
              </select>
            </Col>

            <Col lg={2} md={6} className="mb-2">
              <label className="mb-1 small text-muted">Technician</label>
              <select
                className="form-control form-control-sm"
                value={filters.technician}
                onChange={(e) =>
                  this.handleFilterChange("technician", e.target.value)
                }
              >
                <option value="All Technician">All Technician</option>
                {this.state.technicianList
                  .filter((item) => item !== "กรุณาเลือก")
                  .map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
              </select>
            </Col>

            <Col lg={2} md={6} className="mb-2">
              <label className="mb-1 small text-muted">Doctor</label>
              <select
                className="form-control form-control-sm"
                value={filters.doctor}
                onChange={(e) =>
                  this.handleFilterChange("doctor", e.target.value)
                }
              >
                {!isSleepPhysician && (
                  <option value="All Doctor">All Doctor</option>
                )}
                {doctorOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </Col>

            <Col lg={2} md={6} className="mb-2">
              <label className="mb-1 small text-muted">Date From</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filters.dateFrom}
                onChange={(e) =>
                  this.handleFilterChange("dateFrom", e.target.value)
                }
              />
            </Col>

            <Col lg={2} md={6} className="mb-2">
              <label className="mb-1 small text-muted">Date To</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={filters.dateTo}
                onChange={(e) =>
                  this.handleFilterChange("dateTo", e.target.value)
                }
              />
            </Col>

            <Col lg={2} md={6} className="mb-2 d-flex align-items-end">
              <Button
                variant="light"
                size="sm"
                className="w-100"
                onClick={this.resetFilters}
                style={{ border: "1px solid #ced4da" }}
              >
                Reset
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </div>
    );
  };

  renderDetailRow = (label, value) => (
    <div
      className="mb-2"
      style={{
        display: "grid",
        gridTemplateColumns: "170px 1fr",
        gap: 8,
        fontSize: 13,
      }}
    >
      <div className="text-muted">{label}</div>
      <div style={{ fontWeight: 500, whiteSpace: "pre-wrap" }}>
        {value || value === 0 ? value : "-"}
      </div>
    </div>
  );

  renderDetailSection = (title, children) => {
    return (
      <div
        className="mb-3"
        style={{
          border: "1px solid #e9ecef",
          borderRadius: 10,
          background: "#fff",
          padding: 14,
        }}
      >
        <h6
          className="mb-3"
          style={{
            fontWeight: 700,
            color: "#2f365f",
          }}
        >
          {title}
        </h6>

        {children}
      </div>
    );
  };

  formatNoteDate = (value) => {
    if (!value) return "-";

    if (value.toDate || value.seconds || value instanceof Date) {
      return this.formatAuditDateTime(value);
    }

    const text = String(value || "").trim();
    return text ? text.slice(0, 10) : "-";
  };

  renderNoteCard = ({ title, date, note, mutedText }) => (
    <div
      className="mb-2"
      style={{
        border: "1px solid #e9ecef",
        borderRadius: 8,
        background: "#f8f9fa",
        padding: 10,
        minHeight: 84,
      }}
    >
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div style={{ fontWeight: 700, color: "#2f365f" }}>{title}</div>
        <div className="text-muted small">{this.formatNoteDate(date)}</div>
      </div>
      <div style={{ whiteSpace: "pre-wrap", fontSize: 13 }}>
        {note || <span className="text-muted">{mutedText || "-"}</span>}
      </div>
    </div>
  );

  renderFollowupNotesSection = (item = {}, previous = null) =>
    this.renderDetailSection(
      "Notes",
      <Row>
        <Col lg={6}>
          {this.renderNoteCard({
            title: "Current Note",
            date: item.date || item.followupDate,
            note: item.note,
            mutedText: "No current note",
          })}
        </Col>
        <Col lg={6}>
          {this.renderNoteCard({
            title: "Previous Note",
            date: previous && (previous.date || previous.recordedAt),
            note: previous && previous.note,
            mutedText: "No previous note",
          })}
        </Col>
      </Row>,
    );

  handleEditInputChange = (name, value) => {
    this.setState((prevState) => ({
      editForm: {
        ...prevState.editForm,
        [name]: value,
      },
    }));
  };

  handleEditSideEffectChange = (option, checked) => {
    this.setState((prevState) => {
      const sideEffects = updateCpapSideEffectSelection(
        prevState.editForm.sideEffects,
        option,
        checked,
      );

      return {
        editForm: {
          ...prevState.editForm,
          sideEffects,
        },
      };
    });
  };

  getSelectedNode = (nodeName) => {
    return (
      this.state.nodeList.find((item) => item.nodename === nodeName) || null
    );
  };

  fetchCustomersByNodeName = async (nodeName) => {
    const selectedNode = this.getSelectedNode(nodeName);

    if (!selectedNode) {
      this.setState({ customerList: [] });
      return [];
    }

    try {
      const customerSnap = await getDocs(
        collection(db, "nodes", selectedNode.id, "customers"),
      );

      const loadedCustomers = customerSnap.docs.map((item) => ({
        id: item.id,
        ...item.data(),
        displayName:
          item.data().CustomerNameEN || item.data().customerName || item.id,
      }));

      const customerList = filterCpapFollowupCustomers({
        customers: loadedCustomers, currentUser: this.context && this.context.currentUser,
        adminData: this.state.staffAdminProfile, node: selectedNode,
      });
      this.setState({ customerList });
      return customerList;
    } catch (error) {
      console.error(error);
      this.setState({ customerList: [] });
      return [];
    }
  };

  getLatestTimestampSeconds = (item) => {
    if (item && item.createdAt && item.createdAt.seconds) {
      return item.createdAt.seconds;
    }

    if (item && item.start && item.start.seconds) {
      return item.start.seconds;
    }

    return 0;
  };

  loadPatientCpapFiles = async (hn) => {
    if (!hn) {
      return {
        usageReports: [],
        questionnaires: [],
      };
    }

    try {
      const filesRef = collection(db, "patients", hn, "cpapFollowupFiles");

      let fileSnap;

      try {
        fileSnap = await getDocs(
          query(filesRef, orderBy("uploadedAt", "desc")),
        );
      } catch (error) {
        fileSnap = await getDocs(filesRef);
      }

      const usageReports = [];
      const questionnaires = [];

      fileSnap.forEach((fileDoc) => {
        const data = fileDoc.data();

        const fileData = {
          id: fileDoc.id,
          fileName: data.fileName || "",
          savedFileName: data.savedFileName || "",
          storagePath: data.storagePath || "",
          url: data.url || "",
          uploadedAt: data.uploadedAt || "",
          uploadedBy: data.uploadedBy || "",
          hn: data.hn || hn,
          followupUuid: data.followupUuid || "",
          fileType: data.fileType || "",
        };

        if (data.fileType === "questionnaires") {
          questionnaires.push(fileData);
        } else {
          usageReports.push(fileData);
        }
      });

      return {
        usageReports,
        questionnaires,
      };
    } catch (error) {
      console.error("Load patient CPAP files error:", error);

      return {
        usageReports: [],
        questionnaires: [],
      };
    }
  };

  loadLatestTherapyStatus = async (hn, currentUuid = "") => {
    if (!hn) return null;

    try {
      const followupQ = query(
        collection(db, COLLECTION_NAME),
        where("extendedProps.hn", "==", hn),
      );

      const snap = await getDocs(followupQ);
      const list = [];

      snap.forEach((item) => {
        const data = item.data();
        if (!this.filterAccessibleRecords([data]).length) return;
        const ep = data.extendedProps || {};
        const uuid = ep.uuid || data.id || item.id;

        if (currentUuid && uuid === currentUuid) return;

        list.push({
          uuid,
          date: ep.date || ep.followupDate || toDateString(data.start),
          type: ep.type || "",
          status: ep.status || "",
          minPressure: ep.minPressure || "",
          maxPressure: ep.maxPressure || "",
          currentRampTime: ep.currentRampTime || "",
          doctorActionStatus: ep.doctorActionStatus || "",
          note: ep.note || "",
        });
      });

      list.sort((a, b) =>
        String(b.date || "").localeCompare(String(a.date || "")),
      );

      return list.length > 0 ? list[0] : null;
    } catch (error) {
      console.error("Load latest therapy status error:", error);
      return null;
    }
  };

  onSearchPatient = async () => {
    const hn = safeText(this.state.editForm && this.state.editForm.hn).trim();

    if (!hn) {
      notifyWarning("Please fill HN first");
      return;
    }

    const lookupAccessKey = getCpapFollowupAccessKey(this.context && this.context.currentUser);
    try {
      const patientQ = query(collection(db, "patients"), where("hn", "==", hn));

      const bookingQ = query(
        collectionGroup(db, "bookings"),
        where("extendedProps.hn", "==", hn),
      );

      const [patientSnap, bookingSnap] = await Promise.all([
        getDocs(patientQ),
        getDocs(bookingQ),
      ]);

      if (lookupAccessKey !== getCpapFollowupAccessKey(this.context && this.context.currentUser)) return;

      let patientData = null;

      patientSnap.forEach((item) => {
        if (!patientData) patientData = item.data();
      });

      const bookingList = [];
      bookingSnap.forEach((item) => {
        if (this.filterAccessibleRecords([item.data()]).length) bookingList.push(item.data());
      });

      bookingList.sort((a, b) => {
        const aCreated = this.getLatestTimestampSeconds(a);
        const bCreated = this.getLatestTimestampSeconds(b);
        return bCreated - aCreated;
      });

      const latestBooking = bookingList[0] || null;
      const latestEp =
        latestBooking && latestBooking.extendedProps
          ? latestBooking.extendedProps
          : {};

      const nodeName =
        (patientData && patientData.nodeRef) || latestEp.node || "";
      const patientScope = { node: nodeName, customer: (patientData && (patientData.refCustomer || patientData.customerRef)) || latestEp.customer || "" };
      if (isCpapFollowupNodeCustomerScopedUser(this.context && this.context.currentUser) && !this.filterAccessibleRecords([patientScope]).length) {
        this.setState({ editForm: { ...getEmptyForm(), hn }, customerList: [] });
        notifyWarning("This patient is outside your assigned node/customer access");
        return;
      }
      const selectedNode = this.getSelectedNode(nodeName);
      const nodeId =
        (selectedNode && selectedNode.id) ||
        (nodeName === latestEp.node ? latestEp.nodeId || "" : "");

      if (!patientData) {
        if (nodeName) {
          await this.fetchCustomersByNodeName(nodeName);
        }

        if (lookupAccessKey !== getCpapFollowupAccessKey(this.context && this.context.currentUser)) return;

        this.setState((prevState) => ({
          editForm: {
            ...prevState.editForm,
            node: nodeName,
            nodeId,
            customer: latestEp.customer || "",
          },
        }));
        notifyWarning("New patient or patient not found");
        return;
      }

      if (nodeName) {
        await this.fetchCustomersByNodeName(nodeName);
      }

      const patientFiles = await this.loadPatientCpapFiles(hn);
      const latestTherapyStatus = await this.loadLatestTherapyStatus(
        hn,
        this.state.editForm.uuid,
      );

      if (lookupAccessKey !== getCpapFollowupAccessKey(this.context && this.context.currentUser)) return;

      this.setState((prevState) => ({
        editForm: {
          ...prevState.editForm,
          order: latestEp.order || prevState.editForm.order,
          firstname:
            patientData.firstnameTh ||
            patientData.firstname ||
            prevState.editForm.firstname,
          lastname:
            patientData.lastnameTh ||
            patientData.lastname ||
            prevState.editForm.lastname,
          prePurchaseAhi:
            latestEp.ahi ||
            latestEp.AHI ||
            patientData.ahi ||
            patientData.AHI ||
            patientData.prePurchaseAhi ||
            prevState.editForm.prePurchaseAhi,
          phone: patientData.mobile || prevState.editForm.phone,
          underlyingDisease:
            patientData.comorbidity || prevState.editForm.underlyingDisease,
          hospitalRef:
            patientData.refHn ||
            latestEp.hospitalRef ||
            latestEp.refHospital ||
            latestEp.hospital ||
            prevState.editForm.hospitalRef,
          node: nodeName,
          nodeId,
          customer:
            patientData.refCustomer ||
            patientData.customerRef ||
            latestEp.customer ||
            "",
          usageReports: patientFiles.usageReports,
          questionnaires: patientFiles.questionnaires,
          latestTherapyStatusRecord:
            prevState.editForm.latestTherapyStatusRecord || latestTherapyStatus,
        },
      }));
    } catch (error) {
      console.error(error);
      notifyError("Search patient error");
    }
  };

  handleEditNodeChange = async (nodeName) => {
    const selectedNode = this.getSelectedNode(nodeName);

    this.setState((prevState) => {
      const typeOptions = getFollowupTypeOptionsForNode(
        nodeName,
        prevState.nodeList,
      );

      return {
        editForm: {
          ...prevState.editForm,
          node: nodeName,
          nodeId: selectedNode ? selectedNode.id : "",
          customer: "",
          type: typeOptions.includes(prevState.editForm.type)
            ? prevState.editForm.type
            : typeOptions[0],
        },
      };
    });

    await this.fetchCustomersByNodeName(nodeName);
  };

  handleEditDoctorActionChange = (value) => {
    this.setState((prevState) => {
      const form = prevState.editForm;

      const nextForm = {
        ...form,
        doctorActionStatus: value,
      };

      if (value === "Continue Therapy") {
        nextForm.latestTherapyStatusRecord = this.buildDoctorDecisionRecord(
          form,
          value,
        );

        nextForm.type = this.getNextFollowupTypeForContinueTherapy(
          form.type,
          form.node,
        );
        nextForm.status = "CPAP Follow Up";

        if (form.type === "CPAP Follow-Up 1Y") {
          nextForm.date = this.addDaysToDateString(form.date, 365);
        } else {
          nextForm.date = this.addDaysToDateString(form.date, 90);
        }

        nextForm.doctorActionStatus = "No Doctor Action";
      }

      if (value === "Adjust Pressure") {
        nextForm.latestTherapyStatusRecord = this.buildDoctorDecisionRecord(
          form,
          value,
        );

        nextForm.type = form.type;
        nextForm.status = "Technical Solution";
        nextForm.date = this.addDaysToDateString(form.date, 14);
        nextForm.doctorActionStatus = "Adjust Pressure";
      }

      return {
        editForm: nextForm,
      };
    });
  };

  validateEditForm = () => {
    const { editForm } = this.state;

    if (!editForm) return false;

    if (!safeText(editForm.hn).trim()) {
      notifyError("Please fill HN");
      return false;
    }

    if (
      !safeText(editForm.firstname).trim() &&
      !safeText(editForm.lastname).trim()
    ) {
      notifyError("Please fill patient name");
      return false;
    }

    if (!editForm.date) {
      notifyError("Please select date");
      return false;
    }

    if (
      !isFollowupTypeAllowedForNode(
        editForm.type,
        editForm.node,
        this.state.nodeList,
      )
    ) {
      notifyError("LPH node supports CPAP Follow-Up 1M, 6M, and 1Y only");
      return false;
    }

    return true;
  };

  buildEditPayload = (uuid) => {
    const { editForm } = this.state;
    const statusConfig = getStatusConfig(editForm.status);
    const date = editForm.date || formatLocalDate(new Date());
    const startDate = new Date(`${date}T00:00:00`);

    const extendedProps = {
      uuid,
      order: safeText(editForm.order),
      hn: safeText(editForm.hn),
      firstname: safeText(editForm.firstname),
      lastname: safeText(editForm.lastname),
      phone: safeText(editForm.phone),
      hospitalRef: safeText(editForm.hospitalRef),
      node: safeText(editForm.node),
      nodeId: safeText(editForm.nodeId),
      customer: safeText(editForm.customer),

      underlyingDisease: safeText(editForm.underlyingDisease),
      prePurchaseAhi: safeText(editForm.prePurchaseAhi),

      date,
      followupDate: date,
      type: safeText(editForm.type),
      status: safeText(editForm.status),
      doctorActionStatus: safeText(editForm.doctorActionStatus),
      technicianConsult: safeText(editForm.technicianConsult),
      doctorConsult: safeText(editForm.doctorConsult),

      latestTherapyStatusRecord: editForm.latestTherapyStatusRecord || null,

      serialNumber: safeText(editForm.serialNumber),
      brand: safeText(editForm.brand),
      model: safeText(editForm.model),
      cpapType: safeText(editForm.cpapType),
      maskType: safeText(editForm.maskType),
      maskSize: safeText(editForm.maskSize),

      minPressure: safeText(editForm.minPressure),
      maxPressure: safeText(editForm.maxPressure),
      currentRampTime: safeText(editForm.currentRampTime),
      humidifierSystem: safeText(editForm.humidifierSystem),
      averageLeak: safeText(editForm.averageLeak),

      dataPeriod: safeText(editForm.dataPeriod),
      dateSummary: safeText(editForm.dateSummary),

      usageMoreThan4HoursPercent: safeText(editForm.usageMoreThan4HoursPercent),
      averageUsageHours: safeText(editForm.averageUsageHours),
      averageAhi: safeText(editForm.averageAhi),
      pressure95Percent: safeText(editForm.pressure95Percent),
      leakRate: safeText(editForm.leakRate),
      leakAssessment: safeText(editForm.leakAssessment),
      essScore: safeText(editForm.essScore),
      currentWeight: safeText(editForm.currentWeight),
      weightChange: safeText(editForm.weightChange),

      wakeFeeling: safeText(editForm.wakeFeeling),
      daytimeSleepiness: safeText(editForm.daytimeSleepiness),

      sideEffects: Array.isArray(editForm.sideEffects)
        ? editForm.sideEffects
        : [],
      sideEffectsOther: safeText(editForm.sideEffectsOther),

      usageReports: Array.isArray(editForm.usageReports)
        ? editForm.usageReports
        : [],

      questionnaires: Array.isArray(editForm.questionnaires)
        ? editForm.questionnaires
        : [],

      annualMaskPurchase: editForm.annualMaskPurchase || null,

      note: safeText(editForm.note),
    };

    return {
      id: uuid,
      title: this.buildTitle(extendedProps),
      start: Timestamp.fromDate(startDate),
      allDay: true,
      color: statusConfig.color,
      extendedProps,
    };
  };

  mapPayloadToTableItem = (payload, oldItem = {}) => {
    const ep = payload.extendedProps || {};
    const patientName = `${safeText(ep.firstname)} ${safeText(
      ep.lastname,
    )}`.trim();

    return {
      ...getEmptyForm(),
      ...oldItem,
      ...ep,
      uuid: ep.uuid || payload.id,
      id: payload.id,
      title: payload.title,
      date: ep.date || toDateString(payload.start),
      patientName,
      createdBy: oldItem.createdBy || this.getCurrentUserName(),
      createdAt: oldItem.createdAt || new Date(),
      updatedBy: this.getCurrentUserName(),
      updatedAt: new Date(),
    };
  };

  onSaveEdit = async () => {
    if (this.isStaffReadOnly()) {
      notifyWarning("Staff can view CPAP follow-up only");
      return;
    }

    if (!this.validateEditForm()) return;

    try {
      this.setState({ isSaving: true });

      const currentUserName = this.getCurrentUserName();
      const isCreate = this.state.isCreateMode || !this.state.editForm.uuid;
      const uuid = isCreate
        ? doc(collection(db, COLLECTION_NAME)).id
        : this.getFollowupDocId(this.state.editForm);

      if (!uuid) {
        notifyError("Cannot save CPAP follow-up: missing document id");
        this.setState({ isSaving: false });
        return;
      }

      const payload = cleanForFirestore(this.buildEditPayload(uuid));
      const refDoc = doc(db, COLLECTION_NAME, uuid);

      if (isCreate) {
        await setDoc(refDoc, {
          ...payload,
          createdBy: currentUserName,
          createdAt: serverTimestamp(),
          updatedBy: currentUserName,
          updatedAt: serverTimestamp(),
        });
      } else {
        await updateDoc(refDoc, {
          ...payload,
          updatedBy: currentUserName,
          updatedAt: serverTimestamp(),
        });
      }

      await this.addFollowupRecordSafely(
        payload,
        isCreate ? "create" : this.getFollowupRecordAction(payload, "update"),
        currentUserName,
      );

      const updatedItem = this.mapPayloadToTableItem(
        payload,
        this.state.selectedFollowup || {},
      );

      this.setState({
        isSaving: false,
        isCreateMode: false,
        isEditMode: false,
        selectedFollowup: updatedItem,
        editForm: { ...updatedItem },
      });

      notifyTopRight(isCreate ? "Create success" : "Update success");

      await this.fetchData();
    } catch (error) {
      console.error(error);
      this.setState({ isSaving: false });
      notifyError("Cannot save CPAP follow-up");
    }
  };

  onRemoveFollowup = async () => {
    if (this.isStaffReadOnly()) {
      notifyWarning("Staff can view CPAP follow-up only");
      return;
    }

    const item = this.state.selectedFollowup;

    if (!item || !item.uuid) return;

    swal({
      title: "Are you sure to delete?",
      text: "Once deleted, you will not be able to recover this CPAP follow-up event.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (!willDelete) return;

      try {
        this.setState({ isSaving: true });

        await deleteDoc(doc(db, COLLECTION_NAME, item.uuid));

        notifyTopRight("Delete success");

        this.closeDetailModal();
        await this.fetchData();

        this.setState({ isSaving: false });
      } catch (error) {
        console.error(error);
        this.setState({ isSaving: false });
        notifyError("Delete error");
      }
    });
  };

  getFileExtension = (fileData) => {
    let fileName = "";

    if (fileData && fileData.fileName) {
      fileName = fileData.fileName;
    } else if (fileData && fileData.storagePath) {
      fileName = fileData.storagePath;
    }

    const cleanName = String(fileName).split("?")[0];
    const parts = cleanName.split(".");

    if (parts.length <= 1) return "";

    return parts[parts.length - 1].toLowerCase();
  };

  getEditFileUrl = async (fileData) => {
    if (!fileData) return "";

    if (fileData.url) {
      return fileData.url;
    }

    if (fileData.storagePath) {
      return await getDownloadURL(ref(storage, fileData.storagePath));
    }

    return "";
  };

  openFilePreviewModal = async (fileData) => {
    try {
      const url = await this.getEditFileUrl(fileData);

      if (!url) {
        notifyError("Cannot open file");
        return;
      }

      this.setState({
        previewFile: {
          ...fileData,
          url,
          extension: this.getFileExtension(fileData),
        },
        isFilePreviewModal: true,
      });
    } catch (error) {
      console.error(error);
      notifyError("Cannot open file");
    }
  };

  closeFilePreviewModal = () => {
    this.setState({
      isFilePreviewModal: false,
      previewFile: null,
    });
  };

  openPreviewFileNewTab = async () => {
    try {
      const fileData = this.state.previewFile;

      if (!fileData) return;

      const url = await this.getEditFileUrl(fileData);

      if (url) {
        window.open(url, "_blank");
      } else {
        notifyError("Cannot open file");
      }
    } catch (error) {
      console.error(error);
      notifyError("Cannot open file");
    }
  };

  openEditFileNewTab = async (fileData) => {
    this.openFilePreviewModal(fileData);
  };

  uploadEditFiles = async (fileType) => {
    if (this.isStaffReadOnly()) {
      notifyWarning("Staff can view CPAP follow-up only");
      return;
    }

    const isUsage = fileType === "usageReports";
    const inputRef = isUsage ? this.usageFileRef : this.questionnaireFileRef;

    const files =
      inputRef && inputRef.current && inputRef.current.files
        ? Array.from(inputRef.current.files)
        : [];

    const { editForm } = this.state;

    if (!editForm || !editForm.uuid) {
      notifyWarning("Please save the follow-up record before upload file");
      return;
    }

    if (!editForm.hn) {
      notifyWarning("HN is empty");
      return;
    }

    if (files.length === 0) {
      notifyWarning("No files selected");
      return;
    }

    const folder = isUsage ? "reports" : "questionnaires";
    const docId = this.getFollowupDocId(editForm);

    try {
      this.setState({ isUploading: true });

      const uploadTasks = files.map((file) => {
        return new Promise((resolve, reject) => {
          const safeName = `${Date.now()}_${file.name}`;
          const storagePath = `cpapfollowup/${editForm.hn}/${folder}/${safeName}`;
          const storageRef = ref(storage, storagePath);
          const task = uploadBytesResumable(storageRef, file);

          task.on(
            "state_changed",
            () => { },
            (error) => reject(error),
            async () => {
              try {
                const url = await getDownloadURL(task.snapshot.ref);

                const fileData = {
                  fileName: file.name,
                  savedFileName: safeName,
                  storagePath,
                  url,
                  uploadedAt: new Date().toISOString(),
                  uploadedBy: this.getCurrentUserName(),
                  hn: editForm.hn,
                  fileType,
                  followupUuid: docId || editForm.uuid,
                };

                const patientFileRef = await addDoc(
                  collection(db, "patients", editForm.hn, "cpapFollowupFiles"),
                  {
                    ...fileData,
                    uploadedAt: serverTimestamp(),
                  },
                );

                resolve({
                  ...fileData,
                  id: patientFileRef.id,
                });
              } catch (error) {
                reject(error);
              }
            },
          );
        });
      });

      await Promise.all(uploadTasks);

      const patientFiles = await this.loadPatientCpapFiles(editForm.hn);

      await updateDoc(doc(db, COLLECTION_NAME, docId || editForm.uuid), {
        "extendedProps.usageReports": patientFiles.usageReports,
        "extendedProps.questionnaires": patientFiles.questionnaires,
        updatedBy: this.getCurrentUserName(),
        updatedAt: serverTimestamp(),
      });

      this.setState((prevState) => {
        const updatedEditForm = {
          ...prevState.editForm,
          usageReports: patientFiles.usageReports,
          questionnaires: patientFiles.questionnaires,
        };

        const updatedSelectedFollowup = prevState.selectedFollowup
          ? {
            ...prevState.selectedFollowup,
            usageReports: patientFiles.usageReports,
            questionnaires: patientFiles.questionnaires,
          }
          : updatedEditForm;

        return {
          isUploading: false,
          editForm: updatedEditForm,
          selectedFollowup: updatedSelectedFollowup,
        };
      });

      if (inputRef.current) {
        inputRef.current.value = "";
      }

      notifyTopRight("Upload success");

      await this.fetchData();
    } catch (error) {
      console.error(error);
      this.setState({ isUploading: false });
      notifyError("Upload error");
    }
  };
  renderFilePreviewModal = () => {
    const file = this.state.previewFile;

    if (!this.state.isFilePreviewModal || !file) return null;

    const extension = this.getFileExtension(file);
    const url = file.url || "";
    const lowerExt = extension.toLowerCase();

    const isPdf = lowerExt === "pdf";

    const isImage =
      lowerExt === "jpg" ||
      lowerExt === "jpeg" ||
      lowerExt === "png" ||
      lowerExt === "gif" ||
      lowerExt === "webp" ||
      lowerExt === "bmp";

    const isWord =
      lowerExt === "doc" ||
      lowerExt === "docx" ||
      lowerExt === "rtf" ||
      lowerExt === "odt";

    let previewContent = null;

    if (isPdf) {
      previewContent = (
        <iframe
          title="PDF Preview"
          src={url}
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            backgroundColor: "#fff",
          }}
        />
      );
    } else if (isImage) {
      previewContent = (
        <div
          style={{
            width: "100%",
            height: "100%",
            overflow: "auto",
            backgroundColor: "#111",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 12,
          }}
        >
          <img
            src={url}
            alt={file.fileName || "Preview"}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
            }}
          />
        </div>
      );
    } else if (isWord) {
      const officeViewerUrl =
        "https://view.officeapps.live.com/op/embed.aspx?src=" +
        encodeURIComponent(url);

      previewContent = (
        <iframe
          title="Word Preview"
          src={officeViewerUrl}
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            backgroundColor: "#fff",
          }}
        />
      );
    } else {
      previewContent = (
        <div
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#fff",
            padding: 24,
            textAlign: "center",
          }}
        >
          <h5 style={{ marginBottom: 8 }}>Preview not available</h5>
          <p style={{ color: "#6c757d", marginBottom: 16 }}>
            This file type cannot be previewed in the browser.
          </p>
          <Button variant="primary" onClick={this.openPreviewFileNewTab}>
            Open File
          </Button>
        </div>
      );
    }

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 99999,
          backgroundColor: "rgba(0,0,0,0.72)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1vh 1vw",
        }}
      >
        <div
          style={{
            width: "98vw",
            height: "98vh",
            backgroundColor: "#fff",
            borderRadius: 8,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 12px 40px rgba(0,0,0,0.35)",
          }}
        >
          <div
            style={{
              minHeight: 58,
              padding: "8px 14px",
              borderBottom: "1px solid #e9ecef",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              backgroundColor: "#fff",
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 15 }}>File Preview</div>
              <div
                style={{
                  fontSize: 12,
                  color: "#6c757d",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={file.fileName || file.storagePath || ""}
              >
                {file.fileName || file.storagePath || ""}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flex: "0 0 auto",
              }}
            >
              <Button
                variant="outline-primary"
                size="sm"
                onClick={this.openPreviewFileNewTab}
              >
                Open New Tab
              </Button>

              <Button
                variant="light"
                size="sm"
                onClick={this.closeFilePreviewModal}
                style={{
                  border: "1px solid #ced4da",
                  minWidth: 38,
                }}
              >
                ✕
              </Button>
            </div>
          </div>

          <div
            style={{
              flex: 1,
              minHeight: 0,
              backgroundColor: "#f8f9fa",
              overflow: "hidden",
            }}
          >
            {previewContent}
          </div>
        </div>
      </div>
    );
  };

  renderEditTextInput = (label, name, placeholder = "", type = "text") => {
    const { editForm } = this.state;

    return (
      <div className="form-group row mb-1">
        <label
          className="col-lg-4 col-form-label"
          style={{ fontSize: 12, paddingTop: 4, paddingBottom: 4 }}
        >
          {label}
        </label>
        <div className="col-lg-8">
          <input
            type={type}
            className="form-control form-control-lg"
            placeholder={placeholder || label}
            value={safeText(editForm[name])}
            onChange={(e) => this.handleEditInputChange(name, e.target.value)}
            style={{
              height: 28,
              minHeight: 28,
              fontSize: 12,
              padding: "3px 8px",
            }}
          />
        </div>
      </div>
    );
  };

  renderEditTextArea = (label, name, rows = 3, width = "4") => {
    const { editForm } = this.state;

    return (
      <div className="form-group row mb-1">
        <label
          className={`col-lg-${width} col-form-label`}
          style={{ fontSize: 12, paddingTop: 4, paddingBottom: 4 }}
        >
          {label}
        </label>
        <div className={`col-lg-${12 - width}`}>
          <textarea
            className="form-control form-control-lg"
            rows={rows}
            value={safeText(editForm[name])}
            onChange={(e) => this.handleEditInputChange(name, e.target.value)}
            style={{
              fontSize: 12,
              padding: "4px 8px",
              lineHeight: 1.35,
            }}
          />
        </div>
      </div>
    );
  };

  renderEditSelect = (label, name, options, onChange = null) => {
    const { editForm } = this.state;

    return (
      <div className="form-group row mb-1">
        <label
          className="col-lg-4 col-form-label"
          style={{ fontSize: 12, paddingTop: 4, paddingBottom: 4 }}
        >
          {label}
        </label>
        <div className="col-lg-8">
          <select
            className="form-control form-control-lg"
            value={safeText(editForm[name])}
            onChange={(e) => {
              if (onChange) onChange(e.target.value);
              else this.handleEditInputChange(name, e.target.value);
            }}
            style={{
              height: 28,
              minHeight: 28,
              fontSize: 12,
              padding: "3px 8px",
            }}
          >
            {options.map((option) => {
              if (typeof option === "string") {
                return (
                  <option key={option} value={option}>
                    {option}
                  </option>
                );
              }

              return (
                <option key={option.value} value={option.value}>
                  {option.label || option.value}
                </option>
              );
            })}
          </select>
        </div>
      </div>
    );
  };

  renderEditRadioGroup = (label, name, options) => {
    const { editForm } = this.state;

    return (
      <div className="form-group row mb-2">
        <label
          className="col-lg-4 col-form-label"
          style={{ fontSize: 12, paddingTop: 4, paddingBottom: 4 }}
        >
          {label}
        </label>
        <div className="col-lg-8" style={{ fontSize: 12 }}>
          {options.map((option) => (
            <label key={option} className="mr-3 mb-1">
              <input
                type="radio"
                className="mr-1"
                checked={editForm[name] === option}
                onChange={() => this.handleEditInputChange(name, option)}
              />
              {option}
            </label>
          ))}
        </div>
      </div>
    );
  };

  renderEditSectionTitle = (title, subtitle = "") => (
    <div className="mb-2 mt-2">
      <h6 className="mb-0" style={{ fontWeight: 700, fontSize: 13 }}>
        {title}
      </h6>
      {subtitle && <small className="text-muted">{subtitle}</small>}
    </div>
  );

  renderEditUploadReportSection = ({
    title,
    files,
    fileType,
    inputRef,
    uploadLabel = "+ Upload",
  }) => {
    const canUpload =
      Boolean(this.state.editForm && this.state.editForm.uuid) &&
      !this.state.isUploading &&
      !this.state.isSaving &&
      !this.isStaffReadOnly();

    const role = this.getCurrentRole();
    const isBlockedRole =
      ["sale", "accountant", "staff"].includes(role) ||
      this.isStaffReadOnly();

    const handleUploadClick = () => {
      if (!this.state.editForm || !this.state.editForm.uuid) {
        notifyWarning("Please save the follow-up record before upload file");
        return;
      }

      if (inputRef.current) {
        inputRef.current.click();
      }
    };

    const renderUploadButton = () => {
      if (isBlockedRole) return null;

      return (
        <Fragment>
          <Button
            onClick={handleUploadClick}
            className="btn-xxs btn-primary"
            disabled={!canUpload}
          >
            {this.state.isUploading ? "Uploading..." : uploadLabel}
          </Button>

          <input
            type="file"
            multiple
            ref={inputRef}
            style={{ display: "none" }}
            onChange={() => this.uploadEditFiles(fileType)}
          />
        </Fragment>
      );
    };

    return (
      <div
        className="mb-3"
        style={{
          border: "1px solid #e9ecef",
          borderRadius: 8,
          padding: 12,
          background: "#fff",
        }}
      >
        <div
          className="d-flex justify-content-between align-items-center mb-2"
          style={{ gap: 12 }}
        >
          <div>
            <div style={{ fontWeight: 700 }}>{title}</div>
            <small className="text-muted">
              {this.state.editForm && this.state.editForm.uuid
                ? "Upload and open file from patient record"
                : "Save this follow-up record before upload file"}
            </small>
          </div>

          {(!files || files.length === 0) && renderUploadButton()}
        </div>

        {files && files.length > 0 ? (
          <div>
            {files.map((file, fileIndex) => (
              <div
                key={`${file.storagePath || file.fileName}-${fileIndex}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 56px 90px",
                  alignItems: "center",
                  gap: 12,
                  padding: "6px 0",
                  width: "100%",
                  borderTop: fileIndex === 0 ? "0" : "1px solid #f1f1f1",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    minWidth: 0,
                    gap: 8,
                  }}
                >
                  <i
                    className="flaticon-381-file-2"
                    style={{
                      color: "#dd0102",
                      flex: "0 0 auto",
                    }}
                  />

                  <button
                    type="button"
                    onClick={() => this.openEditFileNewTab(file)}
                    style={{
                      border: "none",
                      background: "transparent",
                      padding: 0,
                      color: "#007bff",
                      minWidth: 0,
                      textAlign: "left",
                      cursor: "pointer",
                    }}
                    title={file.fileName || file.storagePath || ""}
                  >
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                        maxWidth: "100%",
                      }}
                    >
                      {file.fileName || file.storagePath || "-"}
                    </span>
                  </button>
                </div>

                <div style={{ display: "flex", justifyContent: "center" }}>
                  <button
                    type="button"
                    className="btn btn-info shadow btn-xs sharp"
                    onClick={() => this.openEditFileNewTab(file)}
                    title="Open file"
                  >
                    <i className="fa fa-eye" />
                  </button>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  {fileIndex === 0 ? renderUploadButton() : <span />}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            className="d-flex justify-content-between align-items-center"
            style={{
              paddingTop: 6,
              borderTop: "1px solid #f1f1f1",
            }}
          >
            <div className="text-muted small">No File</div>
          </div>
        )}
      </div>
    );
  };

  renderEditTabs = () => {
    const tabs = [
      { key: "patient", label: "Patient" },
      { key: "device", label: "Device" },
      { key: "therapy", label: "Therapy Data" },
      { key: "symptoms", label: "Symptoms" },
      { key: "annualMaskPurchase", label: "Annual Mask Purchase" },
    ];

    return (
      <ul className="nav nav-tabs mb-3">
        {tabs.map((tab) => (
          <li className="nav-item" key={tab.key}>
            <button
              type="button"
              className={`nav-link ${this.state.activeTab === tab.key ? "active" : ""
                }`}
              onClick={() => this.setState({ activeTab: tab.key })}
              style={{ cursor: "pointer" }}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>
    );
  };

  renderEditPatientTab = () => {
    const nodeOptions = [
      "กรุณาเลือก",
      ...this.state.nodeList.map((item) => item.nodename),
    ];

    return (
      <Row>
        <Col lg={6}>
          {this.renderEditSectionTitle("Patient", "Basic patient information")}
          {this.renderEditTextInput("Order", "order")}
          {this.renderEditSelect(
            "Node",
            "node",
            nodeOptions,
            this.handleEditNodeChange,
          )}
          {this.renderEditSelect("Customer", "customer", [
            "กรุณาเลือก",
            ...this.state.customerList.map(
              (item) => item.displayName || item.CustomerNameEN || item.id,
            ),
          ])}

          <div className="form-group row mb-1">
            <label
              className="col-lg-4 col-form-label"
              style={{ fontSize: 12, paddingTop: 4, paddingBottom: 4 }}
            >
              HN
            </label>
            <div className="col-lg-5">
              <input
                type="text"
                className="form-control form-control-lg"
                value={safeText(this.state.editForm.hn)}
                onChange={(e) =>
                  this.handleEditInputChange("hn", e.target.value)
                }
                style={{
                  height: 28,
                  minHeight: 28,
                  fontSize: 12,
                  padding: "3px 8px",
                }}
              />
            </div>
            <div className="col-lg-3">
              <Button
                className="btn btn-secondary btn-sm w-100"
                onClick={this.onSearchPatient}
                disabled={this.state.isSaving || this.state.isUploading}
                style={{
                  height: 28,
                  minHeight: 28,
                  fontSize: 12,
                  padding: "3px 8px",
                }}
              >
                Search
              </Button>
            </div>
          </div>
          {this.renderEditTextInput("Firstname", "firstname")}
          {this.renderEditTextInput("Lastname", "lastname")}
          {this.renderEditTextInput("Ref HN / Hospital Ref", "hospitalRef")}
          {this.renderEditTextInput("Phone", "phone")}
          {this.renderEditTextArea("โรคประจำตัว", "underlyingDisease", 2, "4")}
          {this.renderEditTextInput(
            "AHI ก่อนใช้เครื่อง",
            "prePurchaseAhi",
            "AHI",
            "number",
          )}
        </Col>

        <Col lg={6}>
          {this.renderEditSectionTitle(
            "Follow-Up",
            "Schedule, status, and responsible staff",
          )}

          {this.renderEditTextInput("Date", "date", "yyyy-mm-dd", "date")}

          {this.renderEditSelect(
            "Follow-Up Type",
            "type",
            getFollowupTypeOptionsForNode(
              this.state.editForm.node,
              this.state.nodeList,
            ),
          )}

          {this.renderEditSelect(
            "Status",
            "status",
            STATUS_OPTIONS.map((item) => ({
              value: item.value,
              label: item.label,
            })),
          )}

          <div className="form-group row mb-2">
            <label className="col-lg-4 col-form-label"></label>
            <div className="col-lg-8">
              <small className="text-muted">
                {getStatusConfig(this.state.editForm.status).helper}
              </small>
            </div>
          </div>

          {this.renderEditSelect(
            "Technician Consult",
            "technicianConsult",
            this.state.technicianList,
          )}

          {this.renderEditSelect(
            "Doctor Consult",
            "doctorConsult",
            this.state.doctorList,
          )}

          {this.renderEditSelect(
            "Doctor Next Step",
            "doctorActionStatus",
            DOCTOR_ACTION_OPTIONS,
            this.handleEditDoctorActionChange,
          )}
        </Col>

        <Col lg={12} className="mt-3">
          {this.renderEditSectionTitle("Files & Note")}

          <Row>
            <Col lg={6}>
              {this.renderEditUploadReportSection({
                title: "CPAP Usage Report",
                files: this.state.editForm.usageReports,
                fileType: "usageReports",
                inputRef: this.usageFileRef,
                uploadLabel: "+ Upload",
              })}
            </Col>
            <Col lg={6}>
              {this.renderEditUploadReportSection({
                title: "Questionnaire",
                files: this.state.editForm.questionnaires,
                fileType: "questionnaires",
                inputRef: this.questionnaireFileRef,
                uploadLabel: "+ Upload",
              })}
            </Col>
            <Col lg={12} className="mt-2">
              {this.renderFileListSection({
                title: "Sleep Study Reports",
                files: this.state.editForm.sleepStudyReports,
                emptyText: "No Sleep Study Report",
              })}
            </Col>
          </Row>

          {this.renderEditTextArea("Note", "note", 4, "2")}
          {this.renderNoteCard({
            title: "Previous Note",
            date:
              this.state.editForm.latestTherapyStatusRecord &&
              (this.state.editForm.latestTherapyStatusRecord.date ||
                this.state.editForm.latestTherapyStatusRecord.recordedAt),
            note:
              this.state.editForm.latestTherapyStatusRecord &&
              this.state.editForm.latestTherapyStatusRecord.note,
            mutedText: "No previous note",
          })}
        </Col>
      </Row>
    );
  };

  renderEditDeviceTab = () => (
    <Row>
      <Col lg={6}>
        {this.renderEditSectionTitle(
          "CPAP Device",
          "Device and mask information",
        )}
        {this.renderEditTextInput("Serial Number", "serialNumber")}
        {this.renderEditTextInput("Brand", "brand")}
        {this.renderEditTextInput("Model", "model")}
        {this.renderEditSelect("CPAP Type", "cpapType", CPAP_TYPE_OPTIONS)}
        {this.renderEditSelect("Mask Type", "maskType", MASK_TYPE_OPTIONS)}
        {this.renderEditSelect("Mask Size", "maskSize", MASK_SIZE_OPTIONS)}
      </Col>
    </Row>
  );

  renderEditTherapyTab = () => (
    <Row>
      <Col lg={6}>
        {this.renderEditSectionTitle("CPAP Pressure Setting")}
        {this.renderEditTextInput(
          "Min Pressure",
          "minPressure",
          "cmH2O",
          "number",
        )}
        {this.renderEditTextInput(
          "Max Pressure",
          "maxPressure",
          "cmH2O",
          "number",
        )}
        {this.renderEditTextInput(
          "Current Ramp Time",
          "currentRampTime",
          "minutes",
          "number",
        )}
        {this.renderEditTextInput("Humidifier System", "humidifierSystem")}
        {this.renderEditTextInput("Average Leak", "averageLeak")}

        {this.renderEditSectionTitle("Objective Data")}
        {this.renderEditTextInput("Data Period", "dataPeriod")}
        {this.renderEditTextInput("Date Summary", "dateSummary")}
      </Col>

      <Col lg={6}>
        {this.renderEditSectionTitle("Compliance")}
        {this.renderEditTextInput(
          "% การใช้งานที่มากกว่า 4 ชม.",
          "usageMoreThan4HoursPercent",
          "%",
          "number",
        )}
        {this.renderEditTextInput(
          "ชั่วโมงการใช้งานเฉลี่ย",
          "averageUsageHours",
          "hours/night",
          "number",
        )}

        {this.renderEditSectionTitle("Efficiency")}
        {this.renderEditTextInput(
          "AHI เฉลี่ยต่อ ชม",
          "averageAhi",
          "events/hour",
          "number",
        )}
        {this.renderEditTextInput(
          "แรงดัน 95% สำหรับ APAP",
          "pressure95Percent",
          "cmH2O",
          "number",
        )}

        {this.renderEditSectionTitle("Mask Leak")}
        {this.renderEditTextInput("Leak Rate", "leakRate")}
        {this.renderEditSelect(
          "การประเมิน",
          "leakAssessment",
          LEAK_ASSESSMENT_OPTIONS,
        )}
      </Col>
    </Row>
  );

  renderEditSymptomsTab = () => (
    <Row>
      <Col lg={6}>
        {this.renderEditSectionTitle("Questionnaire")}
        {this.renderEditTextInput(
          "ข้อมูลคะแนนความง่วง ESS",
          "essScore",
          "ESS score",
          "number",
        )}
        {this.renderEditTextInput(
          "น้ำหนักปัจจุบัน",
          "currentWeight",
          "เช่น 72 kg",
        )}
        {this.renderEditTextInput("การเปลี่ยนแปลงน้ำหนัก", "weightChange")}

        {this.renderEditSectionTitle("Overall Symptoms")}
        {this.renderEditRadioGroup(
          "ความรู้สึกหลังตื่นนอน",
          "wakeFeeling",
          WAKE_FEELING_OPTIONS,
        )}
        {this.renderEditRadioGroup(
          "อาการง่วงนอนตอนกลางวัน",
          "daytimeSleepiness",
          DAYTIME_SLEEPINESS_OPTIONS,
        )}
      </Col>

      <Col lg={6}>
        {this.renderEditSectionTitle("ปัญหาและผลข้างเคียงที่พบ")}
        <div className="pl-2">
          {getCpapSideEffectOptionsForDisplay().map((option) => (
            <div className="form-check mb-2" key={option}>
              <input
                className="form-check-input"
                type="checkbox"
                id={`table-side-effect-${option}`}
                checked={this.state.editForm.sideEffects.includes(option)}
                onChange={(e) =>
                  this.handleEditSideEffectChange(option, e.target.checked)
                }
              />
              <label
                className="form-check-label"
                htmlFor={`table-side-effect-${option}`}
              >
                {option}
              </label>
            </div>
          ))}
          {getSavedNonPdfCpapSideEffectValues(
            this.state.editForm.sideEffects,
          ).length > 0 && (
            <small className="text-muted d-block mt-2">
              ค่าที่บันทึกไว้จากรายการเดิม (เก็บไว้โดยไม่แก้ไข):{" "}
              {getSavedNonPdfCpapSideEffectValues(
                this.state.editForm.sideEffects,
              ).join(", ")}
            </small>
          )}
        </div>
        {this.renderEditTextArea("อื่นๆ", "sideEffectsOther", 3, "4")}
      </Col>
    </Row>
  );

  handleAnnualMaskCaseLinked = (annualMaskPurchase) => {
    this.setState((previousState) => ({
      editForm: {
        ...previousState.editForm,
        annualMaskPurchase,
      },
    }));
  };

  renderEditAnnualMaskPurchaseTab = () => (
    <AnnualMaskPurchaseEventSection
      followup={this.state.editForm || {}}
      readOnly={this.isStaffReadOnly()}
      onCaseLinked={this.handleAnnualMaskCaseLinked}
    />
  );

  renderEditActiveTab = () => {
    switch (this.state.activeTab) {
      case "device":
        return this.renderEditDeviceTab();
      case "therapy":
        return this.renderEditTherapyTab();
      case "symptoms":
        return this.renderEditSymptomsTab();
      case "annualMaskPurchase":
        return this.renderEditAnnualMaskPurchaseTab();
      case "patient":
      default:
        return this.renderEditPatientTab();
    }
  };

  renderReadOnlyDetailBody = (item) => {
    if (!item) return null;

    const previous = item.latestTherapyStatusRecord;

    return (
      <Fragment>
        <div
          className="mb-3 p-3"
          style={{
            border: "1px solid #e9ecef",
            borderRadius: 12,
            background: "#f8f9fa",
          }}
        >
          <div className="d-flex justify-content-between align-items-start flex-wrap">
            <div style={{ minWidth: 0, flex: 1, paddingRight: 16 }}>
              <h5
                className="mb-1"
                style={{ fontWeight: 700, color: "#2f365f" }}
              >
                {item.order || "-"}
              </h5>
              <div className="text-muted small">
                {item.patientName || "-"} · HN: {item.hn || "-"}
              </div>
              <div className="text-muted small">
                Ref HN: {item.hospitalRef || "-"}
              </div>
              <div className="text-muted small">
                Node: {item.node || "-"} · Customer: {item.customer || "-"}
              </div>
            </div>
            <div className="text-right">
              {this.renderStatusBadge(item.status)}
              <div className="small text-muted mt-1">{item.type || "-"}</div>
              <div className="small text-muted">
                Doctor: {getDoctorActionLabel(item.doctorActionStatus)}
              </div>
            </div>
          </div>
        </div>

        <Row>
          <Col lg={6}>
            {this.renderDetailSection(
              "Patient Information",
              <Fragment>
                {this.renderDetailRow("Date", item.date)}
                {this.renderDetailRow("Order", item.order)}
                {this.renderDetailRow("Name", item.patientName)}
                {this.renderDetailRow("HN", item.hn)}
                {this.renderDetailRow("Ref HN", item.hospitalRef)}
                {this.renderDetailRow("Phone", item.phone)}
                {this.renderDetailRow("Node", item.node)}
                {this.renderDetailRow("Customer", item.customer)}
                {this.renderDetailRow("โรคประจำตัว", item.underlyingDisease)}
                {this.renderDetailRow(
                  "AHI ก่อนใช้เครื่อง",
                  item.prePurchaseAhi,
                )}
              </Fragment>,
            )}
          </Col>

          <Col lg={6}>
            {this.renderDetailSection(
              "Follow-Up Status",
              <Fragment>
                {this.renderDetailRow("Follow-Up Type", item.type)}
                {this.renderDetailRow("Status", item.status)}
                {this.renderDetailRow(
                  "Doctor Next Step",
                  getDoctorActionLabel(item.doctorActionStatus),
                )}
                {this.renderDetailRow(
                  "Technician Consult",
                  item.technicianConsult,
                )}
                {this.renderDetailRow("Doctor Consult", item.doctorConsult)}
                {this.renderDetailRow("File Count", this.getFileCount(item))}
              </Fragment>,
            )}
          </Col>
        </Row>

        <Row>
          <Col lg={6}>
            {this.renderDetailSection(
              "CPAP Device",
              <Fragment>
                {this.renderDetailRow("Serial Number", item.serialNumber)}
                {this.renderDetailRow("Brand", item.brand)}
                {this.renderDetailRow("Model", item.model)}
                {this.renderDetailRow("CPAP Type", item.cpapType)}
                {this.renderDetailRow("Mask Type", item.maskType)}
                {this.renderDetailRow("Mask Size", item.maskSize)}
              </Fragment>,
            )}
          </Col>

          <Col lg={6}>
            {this.renderDetailSection(
              "Pressure Setting",
              <Fragment>
                {this.renderDetailRow("Min Pressure", item.minPressure)}
                {this.renderDetailRow("Max Pressure", item.maxPressure)}
                {this.renderDetailRow(
                  "Current Ramp Time",
                  item.currentRampTime,
                )}
                {this.renderDetailRow(
                  "Humidifier System",
                  item.humidifierSystem,
                )}
                {this.renderDetailRow("Average Leak", item.averageLeak)}
              </Fragment>,
            )}
          </Col>
        </Row>

        <Row>
          <Col lg={6}>
            {this.renderDetailSection(
              "Objective Data & Compliance",
              <Fragment>
                {this.renderDetailRow("Data Period", item.dataPeriod)}
                {this.renderDetailRow("Date Summary", item.dateSummary)}
                {this.renderDetailRow(
                  "% การใช้งานที่มากกว่า 4 ชม.",
                  item.usageMoreThan4HoursPercent
                    ? `${item.usageMoreThan4HoursPercent}%`
                    : "-",
                )}
                {this.renderDetailRow(
                  "ชั่วโมงการใช้งานเฉลี่ย",
                  item.averageUsageHours,
                )}
              </Fragment>,
            )}
          </Col>

          <Col lg={6}>
            {this.renderDetailSection(
              "Efficiency & Leak",
              <Fragment>
                {this.renderDetailRow("AHI เฉลี่ยต่อ ชม", item.averageAhi)}
                {this.renderDetailRow("แรงดัน 95%", item.pressure95Percent)}
                {this.renderDetailRow("Leak Rate", item.leakRate)}
                {this.renderDetailRow("การประเมิน Leak", item.leakAssessment)}
              </Fragment>,
            )}
          </Col>
        </Row>

        <Row>
          <Col lg={6}>
            {this.renderDetailSection(
              "Questionnaire & Symptoms",
              <Fragment>
                {this.renderDetailRow("ESS Score", item.essScore)}
                {this.renderDetailRow("น้ำหนักปัจจุบัน", item.currentWeight)}
                {this.renderDetailRow(
                  "การเปลี่ยนแปลงน้ำหนัก",
                  item.weightChange,
                )}
                {this.renderDetailRow(
                  "ความรู้สึกหลังตื่นนอน",
                  item.wakeFeeling,
                )}
                {this.renderDetailRow(
                  "อาการง่วงนอนตอนกลางวัน",
                  item.daytimeSleepiness,
                )}
              </Fragment>,
            )}
          </Col>

          <Col lg={6}>
            {this.renderDetailSection(
              "Side Effects",
              <Fragment>
                {item.sideEffects && item.sideEffects.length > 0 ? (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {item.sideEffects.map((effect) => (
                      <Badge
                        key={effect}
                        variant="light"
                        style={{
                          border: "1px solid #ced4da",
                          color: "#495057",
                          padding: "6px 9px",
                          borderRadius: 12,
                          fontSize: 12,
                        }}
                      >
                        {effect}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted small">-</div>
                )}
                {this.renderDetailRow("อื่นๆ", item.sideEffectsOther)}
              </Fragment>,
            )}
          </Col>
        </Row>

        <Row>
          <Col lg={6}>
            {this.renderFileListSection({
              title: "CPAP Usage Report Files",
              files: item.usageReports,
              emptyText: "No Report",
            })}
          </Col>

          <Col lg={6}>
            {this.renderFileListSection({
              title: "Questionnaire Files",
              files: item.questionnaires,
              emptyText: "No Questionnaire",
            })}
          </Col>

          <Col lg={12} className="mt-2">
            {this.renderFileListSection({
              title: "Sleep Study Reports",
              files: item.sleepStudyReports,
              emptyText: "No Sleep Study Report",
            })}
          </Col>
        </Row>

        <div className="mt-3">
          <AnnualMaskPurchaseEventSection followup={item} readOnly />
        </div>

        {this.renderFollowupNotesSection(item, previous)}

        <Row>
          <Col lg={6}>
            {previous
              ? this.renderDetailSection(
                "Previous Doctor Decision",
                <Fragment>
                  {this.renderDetailRow("Date", previous.date)}
                  {this.renderDetailRow("Type", previous.type)}
                  {this.renderDetailRow("Status", previous.status)}
                  {this.renderDetailRow("Min Pressure", previous.minPressure)}
                  {this.renderDetailRow("Max Pressure", previous.maxPressure)}
                  {this.renderDetailRow(
                    "Current Ramp Time",
                    previous.currentRampTime,
                  )}
                  {this.renderDetailRow(
                    "Doctor Next Step",
                    getDoctorActionLabel(previous.doctorActionStatus),
                  )}
                  {this.renderDetailRow("Note", previous.note)}
                </Fragment>,
              )
              : this.renderDetailSection(
                "Previous Doctor Decision",
                <div className="text-muted small">
                  No previous doctor decision
                </div>,
              )}
          </Col>

          <Col lg={6}>
            {this.renderDetailSection(
              "Audit",
              <div className="small text-muted">
                Created: {item.createdBy || "-"} ·{" "}
                {this.formatAuditDateTime(item.createdAt)}
                <br />
                Updated: {item.updatedBy || "-"} ·{" "}
                {this.formatAuditDateTime(item.updatedAt)}
              </div>,
            )}
          </Col>
        </Row>
      </Fragment>
    );
  };

  renderFileListSection = ({ title, files, emptyText = "No File" }) => {
    const fileList = Array.isArray(files) ? files : [];

    return this.renderDetailSection(
      title,
      <Fragment>
        {fileList.length > 0 ? (
          fileList.map((file, index) => (
            <div
              key={`${file.storagePath || file.fileName}-${index}`}
              className="mb-2"
              style={{
                fontSize: 13,
                borderBottom:
                  index === fileList.length - 1 ? "none" : "1px solid #f1f1f1",
                paddingBottom: 6,
              }}
            >
              <i
                className="flaticon-381-file-2 mr-2"
                style={{ color: "#dd0102" }}
              />
              <button
                type="button"
                onClick={() => this.openFilePreviewModal(file)}
                style={{
                  border: "none",
                  background: "transparent",
                  padding: 0,
                  color: "#007bff",
                  cursor: "pointer",
                  textAlign: "left",
                }}
              >
                <span style={{ display: "block" }}>
                  {file.fileName || file.storagePath || "-"}
                </span>
                <small className="text-muted" style={{ display: "block" }}>
                  Uploaded: {this.formatAuditDateTime(file.uploadedAt)}
                  {file.uploadedBy ? ` by ${file.uploadedBy}` : ""}
                </small>
              </button>
            </div>
          ))
        ) : (
          <div className="text-muted small">{emptyText}</div>
        )}
      </Fragment>,
    );
  };

  renderDetailModal = () => {
    const { editForm, selectedFollowup, isEditMode, isCreateMode } = this.state;
    const isStaffReadOnly = this.isStaffReadOnly();
    const title = isCreateMode
      ? "Create CPAP Follow-Up"
      : isEditMode
        ? "Edit CPAP Follow-Up"
        : "CPAP Follow-Up Detail";

    if (!editForm) return null;

    return (
      <Modal
        show={this.state.isDetailModal}
        onHide={this.closeDetailModal}
        size="xl"
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: "75vh", overflowY: "auto" }}>
          {isEditMode || isCreateMode ? (
            <Fragment>
              {this.renderEditTabs()}
              {this.renderEditActiveTab()}
            </Fragment>
          ) : (
            this.renderReadOnlyDetailBody(selectedFollowup)
          )}
        </Modal.Body>

        <Modal.Footer className="justify-content-between">
          <div>
            {!this.state.isCreateMode &&
              this.state.selectedFollowup &&
              this.state.selectedFollowup.uuid &&
              this.getCurrentRole() === "super_admin" && (
                <Button
                  variant="danger"
                  className="mr-2"
                  onClick={this.onRemoveFollowup}
                  disabled={this.state.isSaving || this.state.isUploading}
                >
                  Remove
                </Button>
              )}

            {/* {!this.state.isEditMode && !this.state.isCreateMode && (
              <Button
                variant="secondary"
                onClick={() =>
                  this.setState({
                    isEditMode: true,
                    editForm: { ...this.state.selectedFollowup },
                    activeTab: "patient",
                  })
                }
                disabled={this.state.isSaving || this.state.isUploading}
              >
                Edit
              </Button>
            )} */}
          </div>

          <div>
            {!this.state.isEditMode &&
              !this.state.isCreateMode &&
              !isStaffReadOnly && (
              <Button
                variant="secondary"
                className="mr-2"
                onClick={() =>
                  this.setState({
                    isEditMode: true,
                    editForm: { ...this.state.selectedFollowup },
                    activeTab: "patient",
                  })
                }
                disabled={this.state.isSaving || this.state.isUploading}
              >
                Edit
              </Button>
            )}
            {(isEditMode || isCreateMode) && (
              <Button
                variant="light"
                className="mr-2"
                onClick={() => {
                  if (isCreateMode) {
                    this.closeDetailModal();
                  } else {
                    this.setState({
                      isEditMode: false,
                      editForm: { ...this.state.selectedFollowup },
                    });
                  }
                }}
                disabled={this.state.isSaving || this.state.isUploading}
                style={{ border: "1px solid #ced4da" }}
              >
                Cancel
              </Button>
            )}

            {(isEditMode || isCreateMode) && (
              <Button
                variant="primary"
                onClick={this.onSaveEdit}
                disabled={this.state.isSaving || this.state.isUploading}
              >
                {this.state.isSaving ? "Saving..." : "Save"}
              </Button>
            )}

            {!isEditMode && !isCreateMode && (
              <Button variant="light" onClick={this.closeDetailModal}>
                Close
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
    );
  };

  getVisibleList = () => {
    const { filteredList, currentPage, pageSize } = this.state;
    const startIndex = (currentPage - 1) * pageSize;
    return filteredList.slice(startIndex, startIndex + pageSize);
  };

  renderTable = () => {
    const visibleList = this.getVisibleList();

    return (
      <Card style={{ borderRadius: 10, border: "1px solid #e9ecef" }}>
        <Card.Body style={{ padding: 0 }}>
          <div className="table-responsive">
            <Table hover className="mb-0">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>No.</th>
                  <th
                    style={{ cursor: "pointer", minWidth: 110 }}
                    onClick={() => this.handleSort("date")}
                  >
                    Date {this.renderSortIcon("date")}
                  </th>
                  <th
                    style={{ cursor: "pointer", minWidth: 120 }}
                    onClick={() => this.handleSort("order")}
                  >
                    Order {this.renderSortIcon("order")}
                  </th>
                  <th style={{ minWidth: 190 }}>Patient</th>
                  <th style={{ minWidth: 200 }}>Node</th>
                  <th style={{ minWidth: 150 }}>Status</th>
                  <th style={{ minWidth: 150 }}>Next Step</th>
                  <th style={{ minWidth: 150 }}>Doctor</th>
                  <th style={{ minWidth: 120 }}>AVG. AHI</th>
                  {/* <th
                    style={{ cursor: "pointer", minWidth: 90 }}
                    onClick={() => this.handleSort("fileCount")}
                  >
                    Files {this.renderSortIcon("fileCount")}
                  </th> */}
                  <th style={{ minWidth: 90 }}>Usage &gt;4h</th>

                  <th style={{ width: 110 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {visibleList.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="text-center text-muted py-4">
                      No CPAP follow-up events found
                    </td>
                  </tr>
                ) : (
                  visibleList.map((item, index) => {
                    const no =
                      (this.state.currentPage - 1) * this.state.pageSize +
                      index +
                      1;

                    return (
                      <tr key={item.uuid || item.id}>
                        <td>{no}</td>
                        <td>{item.date || "-"}</td>
                        <td style={{ fontWeight: 600 }}>{item.order || "-"}</td>
                        <td>
                          <div style={{ fontWeight: 600 }}>
                            {item.patientName || "-"}
                          </div>
                          <div className="text-muted small">
                            HN: {item.hn || "-"}
                          </div>
                          <div className="text-muted small">
                            Ref: {item.hospitalRef || "-"}
                          </div>
                        </td>
                        <td>
                          <div>{item.node || "-"}</div>
                          <div className="text-muted small">
                            {item.customer || "-"}
                          </div>
                        </td>
                        <td>{this.renderStatusBadge(item.status)}</td>
                        <td>{getDoctorActionLabel(item.doctorActionStatus)}</td>
                        <td>{item.doctorConsult || "-"}</td>
                        <td>{item.averageAhi || "-"}</td>
                        <td>
                          {item.usageMoreThan4HoursPercent
                            ? `${item.usageMoreThan4HoursPercent}%`
                            : "-"}
                        </td>
                        <td>
                          <Button
                            size="sm"
                            variant="primary"
                            onClick={() => this.openDetailModal(item)}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          {this.renderPagination()}
        </Card.Body>
      </Card>
    );
  };

  render() {
    const isStaffReadOnly = this.isStaffReadOnly();

    return (
      <Fragment>
        <Toast />
        {this.renderFilePreviewModal()}
        {this.renderDetailModal()}

        <Row>
          <Col lg={12}>
            <div>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <Card.Title>CPAP Follow-Up Table</Card.Title>
                {!isStaffReadOnly && (
                  <Button variant="primary" onClick={this.openCreateModal}>
                    + Add Follow-Up
                  </Button>
                )}
              </Card.Header>
              <Card.Body>
                {this.renderSummaryCards()}
                {this.renderFilters()}

                {this.state.isLoading ? (
                  <div className="text-center text-muted py-5">Loading...</div>
                ) : (
                  this.renderTable()
                )}
              </Card.Body>
            </div>
          </Col>
        </Row>
      </Fragment>
    );
  }
}

export default CPAPFollowupTable;
